import { empty, strtoupper, phpCopy } from '../../php-compat';
import type { PortEnv, PhpRef } from '../../port-contract';
import { slot, array, values, isArray, type Arr } from './access';
export interface HealGuardDeps {
    layer_has(row: Arr, name: string): boolean;
    layer_entries(row: Arr, name: string): Arr;
    layer_list_entry_uuid(entry: unknown): string;
    check_uuid(id: unknown): boolean;
    query_full_item_arr(id: string): Arr | null;
    creature_statuses(row: Arr, side: string): Arr;
    item_field(row: Arr, name: string): unknown;
    ft_creature_no_heal_block_reason(row: Arr): string;
}
/** PHP: ft_status_def_forbids_healing (includes/functions/janus/core/heal_target_arr.php:33) */
export function ftStatusDefForbidsHealing(def: Arr | null, env: PortEnv<HealGuardDeps>): boolean {
    if (empty(def) || !isArray(def)) return false;
    if (env.deps.layer_has(def, 'no_healing_rolls')) return true;
    for (const entry of values(env.deps.layer_entries(def, 'status_effect_permanent'))) {
        const id = env.deps.layer_list_entry_uuid(entry);
        if (!env.deps.check_uuid(id)) continue;
        const hit = slot(def, 'catalog', id);
        const nested = isArray(hit) && !empty(slot(hit, 'item_layer_types')) ? hit : env.deps.query_full_item_arr(id);
        if (isArray(nested) && env.deps.layer_has(nested, 'no_healing_rolls')) return true;
    }
    return false;
}
/** PHP: ft_status_no_heal_exemption_met (includes/functions/janus/core/heal_target_arr.php:67) */
export function ftStatusNoHealExemptionMet(def: Arr, creature: Arr, env: PortEnv<HealGuardDeps>): boolean {
    for (const entry of values(env.deps.layer_entries(def, 'if_caster_has_status_effect_not_active'))) {
        const id = env.deps.layer_list_entry_uuid(entry);
        if (env.deps.check_uuid(id) && ftCreatureHasStatusItemIdActive(creature, id, env)) return true;
    }
    return false;
}
/** PHP: ft_creature_holds_status_item (includes/functions/janus/core/heal_target_arr.php:86) */
export function ftCreatureHoldsStatusItem(creature: Arr, id: string, env: PortEnv<HealGuardDeps>): boolean {
    const want = strtoupper(id);
    if (want === '') return false;
    for (const row of values(env.deps.creature_statuses(creature, 'target'))) {
        if (!isArray(row)) continue;
        const got = slot(row, 'status_item_id') ?? env.deps.item_field(row, 'item_id') ?? '';
        if (typeof got === 'string' && strtoupper(got) === want) return true;
    }
    return false;
}
/** PHP: ft_creature_has_status_item_id_active (includes/functions/janus/core/heal_target_arr.php:105) */
export function ftCreatureHasStatusItemIdActive(creature: Arr, id: string, env: PortEnv<HealGuardDeps>): boolean {
    for (const row of values(env.deps.creature_statuses(creature, 'target'))) {
        if (!isArray(row)) continue;
        const got = slot(row, 'status_item_id') ?? env.deps.item_field(row, 'item_id') ?? '';
        if (typeof got === 'string' && got === id) return true;
    }
    return false;
}
/** PHP: ft_creature_cannot_regain_vitality (includes/functions/janus/core/heal_target_arr.php:155) */
export function ftCreatureCannotRegainVitality(creature: Arr, env: PortEnv<HealGuardDeps>): boolean {
    return env.deps.ft_creature_no_heal_block_reason(creature) !== '';
}
/** PHP: ft_nullify_heal_roll_if_blocked (includes/functions/janus/core/heal_target_arr.php:174) */
export function ftNullifyHealRollIfBlocked(creature: Arr, rolls: PhpRef<Arr>, result: PhpRef<unknown>, env: PortEnv<HealGuardDeps>): string {
    const reason = env.deps.ft_creature_no_heal_block_reason(creature);
    if (reason === '') return '';
    result.value = 0;
    const out = array(rolls.value);
    out.append(phpCopy(['∅', 'Healing Nullified · ' + reason]));
    rolls.value = out;
    return reason;
}
